import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Loader2, Plus, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useJobs } from "@/hooks/useJobs";
import { useNavigate } from "react-router-dom";
import { analyzeRoofGeometry } from "@/utils/roofGeometryAnalyzer";
import { useAuth } from "@/hooks/useAuth";
import { getCurrentUserPricingSettings } from "@/lib/pricingSettings";

interface SaveToJobDialogProps {
  measurementData: any;
  estimateData?: {
    totalSquares: number;
    wastePercent: number;
    materialRate: number;
    laborRate: number;
    tearOffCost: number;
    dumpFee: number;
    overheadPercent: number;
    profitPercent: number;
    costSubtotal: number;
    overheadAmount: number;
    profitAmount: number;
    customerPrice: number;
    commissionPercent?: number;
    commissionAmount?: number;
    materialKey?: string;
    materialLabel?: string;
    includeTearoff?: boolean;
    tearoffRatePerSq?: number;
    dumpRatePerSq?: number;
    commissionDollars?: number;
    isRbRestoration?: boolean;
    rawSquares?: number;
  } | null;
}

export function SaveToJobDialog({ measurementData, estimateData }: SaveToJobDialogProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNewJobForm, setShowNewJobForm] = useState(false);
  const [newJobName, setNewJobName] = useState(measurementData?.address || "");
  const [newJobType, setNewJobType] = useState("retail");
  const [savedJobId, setSavedJobId] = useState<string | null>(null);
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false);
  const [newlyCreatedJobId, setNewlyCreatedJobId] = useState<string | null>(null);
  const { user, orgId } = useAuth();
  const { jobs, refetch } = useJobs(orgId || undefined);

  useEffect(() => {
    if (orgId) loadCatalogItems();
  }, [orgId]);

  const loadCatalogItems = async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from("catalog_items")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_active", true)
        .order("name", { ascending: true });

      if (error) throw error;
      setCatalogItems(data || []);
    } catch (error) {
      console.error("Error loading catalog:", error);
    }
  };

  const handleCreateNewJob = async () => {
    if (!newJobName.trim()) {
      toast.error("Job name is required");
      return;
    }

    setSaving(true);
    try {
      if (!user || !orgId) throw new Error("Not authenticated");

      const { data: newJob, error: jobError } = await supabase
        .from('jobs')
        .insert({
          org_id: orgId,
          property_address: newJobName,
          status: 'new',
          latitude: measurementData?.lat,
          longitude: measurementData?.lng,
          property_type: newJobType,
        })
        .select()
        .single();

      if (jobError) throw jobError;

      toast.success("New job created!");
      setSelectedJobId(newJob.id);
      setNewlyCreatedJobId(newJob.id);
      setShowNewJobForm(false);
      await refetch();

      // Immediately push measurement & estimate into this new job
      await saveToJob(newJob.id);

      // Show navigation prompt
      setShowNavigationPrompt(true);
    } catch (error) {
      console.error('Create job error:', error);
      toast.error("Failed to create new job");
    } finally {
      setSaving(false);
    }
  };

  const saveToJob = async (jobId: string) => {
    setSaving(true);
    try {
      if (!user || !orgId) throw new Error("Not authenticated");

      console.log('📊 Saving measurement to job:', {
        jobId,
        address: measurementData.address,
        totalSquares: measurementData.totalSquares
      });

      // Calculate linear measurements for saving
      let linearMeasurements;
      if (measurementData.roofEdgeData) {
        // Prefer high-quality linear measurements already computed from the Solar API
        const edge = measurementData.roofEdgeData;
        linearMeasurements = {
          ridges: edge.totalRidgeLength ?? 0,
          hips: edge.totalHipLength ?? 0,
          valleys: edge.totalValleyLength ?? 0,
          eaves: edge.totalEaveLength ?? 0,
          rakes: edge.totalRakeLength ?? 0,
          totalPerimeter:
            edge.perimeter ??
            (edge.totalRidgeLength ?? 0) +
              (edge.totalHipLength ?? 0) +
              (edge.totalValleyLength ?? 0) +
              (edge.totalEaveLength ?? 0) +
              (edge.totalRakeLength ?? 0),
        };
      } else {
        // Fallback: derive from facet geometry if edge data is missing
        linearMeasurements = analyzeRoofGeometry(
          measurementData.planes.map((p: any) => ({
            id: p.id,
            tiltDegrees: p.tiltDegrees,
            vertices: p.vertices,
          }))
        );
      }
      console.log('📐 Calculated linear measurements for job:', linearMeasurements);

      let measurement;

      if (measurementData.measurementId) {
        const { data: roofingTrade } = await supabase
          .from('job_trades')
          .select('id')
          .eq('job_id', jobId)
          .eq('org_id', orgId)
          .ilike('trade_code', '%roof%')
          .maybeSingle();

        // Clear any prior measurement attached to this job+trade so we don't
        // collide on idx_measurements_roof_trade_unique when re-attaching.
        if (roofingTrade?.id) {
          await supabase
            .from('measurements_roof')
            .update({ job_trade_id: null })
            .eq('job_id', jobId)
            .eq('job_trade_id', roofingTrade.id)
            .neq('id', measurementData.measurementId);
        }

        const { data: updatedMeasurement, error: measurementError } = await supabase
          .from('measurements_roof')
          .update({
            job_id: jobId,
            job_trade_id: roofingTrade?.id ?? null,
            address: measurementData.address,
            total_area: measurementData.totalAreaSqFt,
            total_squares: measurementData.totalSquares,
            ridges: linearMeasurements.ridges,
            hips: linearMeasurements.hips,
            valleys: linearMeasurements.valleys,
            rakes: linearMeasurements.rakes,
            eaves: linearMeasurements.eaves,
            facets: measurementData.planes,
            raw_response: measurementData,
          })
          .eq('id', measurementData.measurementId)
          .eq('org_id', orgId)
          .select()
          .single();

        if (measurementError) throw measurementError;
        measurement = updatedMeasurement;
      } else {
        const { data: insertedMeasurement, error: measurementError } = await supabase
          .from('measurements_roof')
          .insert({
            org_id: orgId,
            job_id: jobId,
            address: measurementData.address,
            total_area: measurementData.totalAreaSqFt,
            total_squares: measurementData.totalSquares,
            ridges: linearMeasurements.ridges,
            hips: linearMeasurements.hips,
            valleys: linearMeasurements.valleys,
            rakes: linearMeasurements.rakes,
            eaves: linearMeasurements.eaves,
            facets: measurementData.planes,
            raw_response: measurementData,
          })
          .select()
          .single();

        if (measurementError) throw measurementError;
        measurement = insertedMeasurement;
      }

      console.log('✅ Measurement saved:', measurement.id);

      // Create or update estimate and get the estimate ID
      let estimateId: string;
      if (estimateData) {
        console.log('📈 Creating estimate from QuickPriceEstimate data...');
        const { data: newEstimate, error: estimateInsertError } = await supabase
          .from('job_estimates')
          .insert({
            org_id: orgId,
            job_id: jobId,
            measurement_id: measurement.id,
            status: 'draft',
            label: 'Estimate from Measurement',
            total_squares: estimateData.totalSquares,
            waste_percent: estimateData.wastePercent,
            material_rate: estimateData.materialRate,
            labor_rate: estimateData.laborRate,
            tear_off_cost: estimateData.tearOffCost,
            dump_fee: estimateData.dumpFee,
            overhead_percent: estimateData.overheadPercent,
            profit_percent: estimateData.profitPercent,
            cost_subtotal: estimateData.costSubtotal,
            overhead_amount: estimateData.overheadAmount,
            profit_amount: estimateData.profitAmount,
            customer_price: estimateData.customerPrice,
          })
          .select()
          .single();

        if (estimateInsertError) throw estimateInsertError;
        estimateId = newEstimate.id;
        console.log('✅ Estimate created from QuickPriceEstimate');
      } else {
        console.log('📈 Creating estimate via RPC...');
        const { data: rpcEstimate, error: estimateError } = await supabase
          .rpc('create_or_update_job_estimate', {
            p_measurement_id: measurement.id,
            p_job_id: jobId,
            p_org_id: orgId,
          });

        if (estimateError) throw estimateError;
        
        // Fetch the created estimate ID
        const { data: fetchedEstimate } = await supabase
          .from('job_estimates')
          .select('id')
          .eq('measurement_id', measurement.id)
          .eq('job_id', jobId)
          .maybeSingle();
        
        estimateId = fetchedEstimate?.id;
        console.log('✅ Estimate created via RPC');
      }

      // Build line items DETERMINISTICALLY from the quote the user just configured.
      // No AI invention — items mirror exactly what was shown on the Measure Quick Quote.
      if (estimateId && estimateData) {
        const items: any[] = [];
        let order = 0;
        const sqWithWaste = Number(estimateData.totalSquares ?? 0);
        const rawSquares = Number(estimateData.rawSquares ?? sqWithWaste);
        const materialLabel = estimateData.materialLabel || 'Roofing Material';
        const matRate = Number(estimateData.materialRate ?? 0);
        const matTotal = sqWithWaste * matRate;

        // 1) Material line
        items.push({
          estimate_id: estimateId,
          item_name: materialLabel,
          description: `Includes ${estimateData.wastePercent ?? 0}% waste (${rawSquares.toFixed(2)} sq + waste = ${sqWithWaste.toFixed(2)} sq)`,
          quantity: Number(sqWithWaste.toFixed(2)),
          unit_type: 'square',
          material_cost: Number(matRate.toFixed(2)),
          labor_cost: 0,
          total_cost: Number(matTotal.toFixed(2)),
          category: 'Materials',
          catalog_item_id: null,
          is_auto_generated: true,
          line_order: order++,
        });

        // 2) Tear-off (only if included in the quote)
        if (estimateData.includeTearoff && Number(estimateData.tearOffCost ?? 0) > 0) {
          const rate = Number(estimateData.tearoffRatePerSq ?? 0);
          items.push({
            estimate_id: estimateId,
            item_name: 'Tear-Off Existing Roof',
            description: `Per-square rate × ${sqWithWaste.toFixed(2)} sq`,
            quantity: Number(sqWithWaste.toFixed(2)),
            unit_type: 'square',
            material_cost: 0,
            labor_cost: Number(rate.toFixed(2)),
            total_cost: Number(Number(estimateData.tearOffCost).toFixed(2)),
            category: 'Labor',
            catalog_item_id: null,
            is_auto_generated: true,
            line_order: order++,
          });
        }

        // 3) Dump fee (only if included in the quote)
        if (estimateData.includeTearoff && Number(estimateData.dumpFee ?? 0) > 0) {
          const rate = Number(estimateData.dumpRatePerSq ?? 0);
          items.push({
            estimate_id: estimateId,
            item_name: 'Dump Fee / Disposal',
            description: `Per-square rate × ${sqWithWaste.toFixed(2)} sq`,
            quantity: Number(sqWithWaste.toFixed(2)),
            unit_type: 'square',
            material_cost: 0,
            labor_cost: Number(rate.toFixed(2)),
            total_cost: Number(Number(estimateData.dumpFee).toFixed(2)),
            category: 'Disposal',
            catalog_item_id: null,
            is_auto_generated: true,
            line_order: order++,
          });
        }

        // 4) Overhead (skip on RB Restoration where overhead = 0)
        if (Number(estimateData.overheadAmount ?? 0) > 0) {
          items.push({
            estimate_id: estimateId,
            item_name: `Overhead (${estimateData.overheadPercent ?? 0}%)`,
            description: 'Applied to subtotal',
            quantity: 1,
            unit_type: 'each',
            material_cost: 0,
            labor_cost: 0,
            total_cost: Number(Number(estimateData.overheadAmount).toFixed(2)),
            category: 'Overhead',
            catalog_item_id: null,
            is_auto_generated: true,
            line_order: order++,
          });
        }

        // 5) Profit (skip on RB Restoration where profit = 0)
        if (Number(estimateData.profitAmount ?? 0) > 0) {
          items.push({
            estimate_id: estimateId,
            item_name: `Profit (${estimateData.profitPercent ?? 0}%)`,
            description: 'Applied to cost + overhead',
            quantity: 1,
            unit_type: 'each',
            material_cost: 0,
            labor_cost: 0,
            total_cost: Number(Number(estimateData.profitAmount).toFixed(2)),
            category: 'Profit',
            catalog_item_id: null,
            is_auto_generated: true,
            line_order: order++,
          });
        }

        // 6) Commission / Rep markup
        if (Number(estimateData.commissionAmount ?? 0) > 0) {
          const isFlat = estimateData.isRbRestoration;
          items.push({
            estimate_id: estimateId,
            item_name: isFlat ? 'Sales Commission' : `Sales Commission (${estimateData.commissionPercent ?? 0}%)`,
            description: isFlat ? 'Flat commission' : 'Applied to customer price',
            quantity: 1,
            unit_type: 'each',
            material_cost: 0,
            labor_cost: 0,
            total_cost: Number(Number(estimateData.commissionAmount).toFixed(2)),
            category: 'Commission',
            catalog_item_id: null,
            is_auto_generated: true,
            line_order: order++,
          });
        }

        const { error: insertError } = await supabase
          .from('estimate_line_items')
          .insert(items);

        if (insertError) {
          console.error('⚠️ Line item insert failed:', insertError);
        } else {
          console.log(`✅ Inserted ${items.length} deterministic line items from quote`);
        }
      } else if (estimateId) {
        // No QuickPriceEstimate data — fall back to AI suggestions
        console.log('🤖 No quote data — falling back to AI line item suggestions...');
        try {
          const pricingSettings = await getCurrentUserPricingSettings(user.id);
          const { data: lineItemsData, error: suggestError } = await supabase.functions.invoke('suggest-estimate-items', {
            body: {
              measurement_data: measurement,
              catalog_items: catalogItems,
              trade_code: 'roofing',
              pricing_settings: pricingSettings,
            },
          });
          if (suggestError) throw suggestError;
          if (lineItemsData?.suggested_items?.length) {
            await supabase.from('estimate_line_items').insert(
              lineItemsData.suggested_items.map((item: any, index: number) => ({
                estimate_id: estimateId,
                item_name: item.item_name,
                description: item.description || null,
                quantity: item.quantity,
                unit_type: item.unit_type,
                material_cost: item.material_cost || 0,
                labor_cost: item.labor_cost || 0,
                total_cost: item.total_cost,
                category: item.category || 'General',
                catalog_item_id: item.catalog_item_id || null,
                is_auto_generated: true,
                line_order: index,
              }))
            );
          }
        } catch (aiError) {
          console.error('⚠️ AI fallback failed:', aiError);
        }
      }

      toast.success("Measurement pushed to job with auto-generated line items!");
      
      // Auto-navigate to Estimates tab with correct tab name
      navigate(`/jobs/${jobId}?tab=Measurement/Estimate`);
      setOpen(false);
      
      // Trigger refresh of parent page
      window.dispatchEvent(new CustomEvent('estimate-created', { detail: { jobId } }));

    } catch (error) {
      console.error('❌ Save error:', error);
      toast.error("Failed to save measurement data");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedJobId) {
      toast.error("Please select a job");
      return;
    }

    await saveToJob(selectedJobId);
  };

  return (
    <>
      <AlertDialog open={showNavigationPrompt} onOpenChange={setShowNavigationPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Job Created Successfully!</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to go to the new job now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowNavigationPrompt(false);
              setNewlyCreatedJobId(null);
            }}>
              Stay Here
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (newlyCreatedJobId) {
                navigate(`/job/${newlyCreatedJobId}`);
                setOpen(false);
                setShowNavigationPrompt(false);
              }
            }}>
              Go to Job
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSavedJobId(null);
          setSelectedJobId("");
          setNewlyCreatedJobId(null);
        }
      }}>
        <DialogTrigger asChild>
          <Button className="gap-2">
            <ArrowUpRight className="w-4 h-4" />
            Push to Job
          </Button>
        </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Send Measurement to Job</DialogTitle>
          <DialogDescription>
            Attach this measurement and create a draft estimate
          </DialogDescription>
        </DialogHeader>

        {savedJobId ? (
          <div className="space-y-6 py-4">
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArrowUpRight className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Successfully Pushed!</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Measurement and estimate have been added to the job
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    navigate(`/jobs/${savedJobId}?tab=Measurement/Estimate`);
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Job Estimate
                </Button>
              </div>
            </div>
          </div>
        ) : !showNewJobForm ? (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Select Job</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger className="h-11 w-full min-w-0 text-left [&>span]:flex [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate">
                  <SelectValue placeholder="Choose a job...">
                    {selectedJobId && (() => {
                      const j = jobs.find((x) => x.id === selectedJobId);
                      if (!j) return null;
                      const name = j.owner_name || j.property_address || "Job";
                      const addr = j.owner_name ? j.property_address : null;
                      return (
                        <span className="flex min-w-0 items-center gap-2 truncate">
                          <span className="font-semibold text-foreground truncate">{name}</span>
                          {addr && (
                            <span className="text-xs text-muted-foreground truncate">· {addr}</span>
                          )}
                        </span>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-background z-50 max-w-[--radix-select-trigger-width]">
                  {jobs.map((job) => (
                    <SelectItem key={job.id} value={job.id} className="py-2">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-semibold text-foreground truncate">
                          {job.owner_name || job.property_address || "Job"}
                        </span>
                        {job.owner_name && job.property_address && (
                          <span className="text-xs text-muted-foreground truncate">
                            {job.property_address}
                          </span>
                        )}
                        {job.property_type && (
                          <span className="text-[11px] text-muted-foreground/80 truncate">
                            ({job.property_type})
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={() => setShowNewJobForm(true)}
            >
              <Plus className="w-4 h-4" />
              Create new job from this measurement
            </Button>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!selectedJobId || saving}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Attach & Create Estimate"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Job Name</Label>
              <Input
                value={newJobName}
                onChange={(e) => setNewJobName(e.target.value)}
                placeholder="Enter job name or address"
              />
            </div>

            <div className="space-y-2">
              <Label>Job Type</Label>
              <Select value={newJobType} onValueChange={setNewJobType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowNewJobForm(false)}
              >
                Back
              </Button>
              <Button
                onClick={handleCreateNewJob}
                disabled={saving || !newJobName.trim()}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Job"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
